import { Search, ArrowBack, NavigateNext, NavigateBefore, Description, Refresh, Delete, Visibility, Close, FilterList, Category, Group } from "@material-ui/icons";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";

import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    ResultsCard,
    SectionInfoTitle,
    SectionSubtitle,
    TableWrapper,
    StreamsTable,
    TableHead,
    TableRow,
    TableCell,
    TableActions,
    EmptyState,
    EmptyTitle,
    EmptyText,
    getTheme,
    ActionStack,
    IconActionButton,
    DangerButton,
} from "../../components/common";

const TYPE_OPTIONS = [
    { value: "", label: "Todos os tipos" },
    { value: "business-partner", label: "Business Partner" },
    { value: "buying-create", label: "Buying Create" },
    { value: "buying-alter", label: "Buying Alter" },
    { value: "buying-cancel", label: "Buying Cancel" },
];

const TYPE_LABELS = {
    "business-partner": "Business Partner",
    "buying-create": "Buying Create",
    "buying-alter": "Buying Alter",
    "buying-cancel": "Buying Cancel",
};

function ResponseErrors() {
    const history = useHistory();
    const theme = getTheme("ariba");

    const [records, setRecords] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    // Filters
    const [filterType, setFilterType] = useState("");
    const [filterGrupoId, setFilterGrupoId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Detail modal
    const [detailRecord, setDetailRecord] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Confirm delete modal
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Load groups for the dropdown
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const { data } = await fetchClient.get("/ariba/getAllGroups");
                setGroups(data || []);
            } catch (error) {
                console.log("Erro ao carregar grupos:", error);
            }
        };
        fetchGroups();
    }, []);

    const fetchRecords = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (filterType) params.append("type", filterType);
            if (filterGrupoId) params.append("grupoId", filterGrupoId);
            const query = params.toString();
            const url = `/ariba/response-errors${query ? `?${query}` : ""}`;
            const { data } = await fetchClient.get(url);
            setRecords(data);
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao carregar registros de erro");
        } finally {
            setIsLoading(false);
        }
    }, [filterType, filterGrupoId]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    // Local search filtering
    const filteredRecords = useMemo(() => {
        if (!searchTerm) return records;
        const term = searchTerm.toLowerCase();
        return records.filter((item) => {
            const typeMatch = item.type && item.type.toLowerCase().includes(term);
            const nameMatch = item.uniqueName && item.uniqueName.toLowerCase().includes(term);
            const errorMatch = item.errorMessage && item.errorMessage.toLowerCase().includes(term);
            return typeMatch || nameMatch || errorMatch;
        });
    }, [records, searchTerm]);

    // Paginated data
    const paginatedRecords = useMemo(() => {
        const sorted = filteredRecords.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const startIndex = (currentPage - 1) * recordsPerPage;
        return sorted.slice(startIndex, startIndex + recordsPerPage);
    }, [filteredRecords, currentPage, recordsPerPage]);

    const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, recordsPerPage, filterType, filterGrupoId]);

    // View detail
    const handleViewDetail = async (record) => {
        try {
            setDetailLoading(true);
            setDetailRecord({ ...record, returnXml: null });
            const { data } = await fetchClient.get(`/ariba/response-errors/${record.id}`);
            setDetailRecord(data);
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao carregar detalhes");
            setDetailRecord(null);
        } finally {
            setDetailLoading(false);
        }
    };

    // Resend
    const handleResend = async (record) => {
        try {
            setActionLoading(record.id);
            await fetchClient.post(`/ariba/response-errors/${record.id}/resend`);
            message.sucess("XML reenviado com sucesso!");
            setRecords((prev) => prev.filter((r) => r.id !== record.id));
            if (detailRecord && detailRecord.id === record.id) {
                setDetailRecord(null);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao reenviar XML ao Ariba");
        } finally {
            setActionLoading(null);
        }
    };

    // Delete
    const handleDelete = async (record) => {
        try {
            setActionLoading(record.id);
            await fetchClient.delete(`/ariba/response-errors/${record.id}`);
            message.sucess("Registro removido com sucesso!");
            setRecords((prev) => prev.filter((r) => r.id !== record.id));
            setDeleteConfirm(null);
            if (detailRecord && detailRecord.id === record.id) {
                setDetailRecord(null);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao remover registro");
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    };

    if (isLoading) return <Loader />;

    return (
        <Container>
            <ModernHeader>
                <HeaderLeft>
                    <BackButton color={theme.primary} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <PageTitle>
                            <Refresh style={{ marginRight: "12px", fontSize: "32px" }} />
                            Reprocessamento de Respostas
                        </PageTitle>
                        <PageSubtitle>Gerencie e reenvie respostas com erro do Ariba</PageSubtitle>
                    </HeaderContent>
                </HeaderLeft>
            </ModernHeader>

            {/* Filters */}
            <FiltersCard>
                <FiltersHeader>
                    <FilterList style={{ fontSize: "20px", color: "#e67e22" }} />
                    <FiltersTitle>Filtros</FiltersTitle>
                </FiltersHeader>
                <FilterRow>
                    <FilterGroup>
                        <FilterLabel>
                            <Category style={{ fontSize: "16px" }} />
                            Tipo
                        </FilterLabel>
                        <FilterSelectWrapper>
                            <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                {TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </FilterSelect>
                        </FilterSelectWrapper>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>
                            <Group style={{ fontSize: "16px" }} />
                            Grupo
                        </FilterLabel>
                        <FilterSelectWrapper>
                            <FilterSelect value={filterGrupoId} onChange={(e) => setFilterGrupoId(e.target.value)}>
                                <option value="">Todos os grupos</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.id}
                                    </option>
                                ))}
                            </FilterSelect>
                        </FilterSelectWrapper>
                    </FilterGroup>
                    <FilterGroup style={{ flex: 1 }}>
                        <FilterLabel>
                            <Search style={{ fontSize: "16px" }} />
                            Busca
                        </FilterLabel>
                        <SearchContainer>
                            <SearchIconWrapper>
                                <Search style={{ fontSize: "20px" }} />
                            </SearchIconWrapper>
                            <SearchInput
                                type="text"
                                placeholder="Pesquisar por tipo, nome ou erro..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <ClearSearchButton onClick={() => setSearchTerm("")}>
                                    ×
                                </ClearSearchButton>
                            )}
                        </SearchContainer>
                    </FilterGroup>
                </FilterRow>
            </FiltersCard>

            {records.length === 0 && !isLoading ? (
                <ResultsCard>
                    <EmptyState>
                        <Description style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "16px" }} />
                        <EmptyTitle>Nenhum erro encontrado</EmptyTitle>
                        <EmptyText>Não há respostas com erro para reprocessar.</EmptyText>
                    </EmptyState>
                </ResultsCard>
            ) : (
                <ResultsCard>
                    <SectionInfoWrapper>
                        <SectionInfoLeft>
                            <SectionInfoTitle>Erros de resposta</SectionInfoTitle>
                            <SectionSubtitle>
                                Mostrando {paginatedRecords.length} de {filteredRecords.length}{" "}
                                {filteredRecords.length === 1 ? "registro" : "registros"}
                                {searchTerm && ` (filtrado de ${records.length})`}
                            </SectionSubtitle>
                        </SectionInfoLeft>
                    </SectionInfoWrapper>

                    <TableWrapper>
                        <StreamsTable minWidth="800px">
                            <thead>
                                <tr>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>UniqueName</TableHead>
                                    <TableHead>Erro</TableHead>
                                    <TableHead>Grupo ID</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead align="right">Ações</TableHead>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedRecords.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} style={{ textAlign: "center", color: "#6b7280" }}>
                                            Nenhum resultado encontrado para os filtros aplicados
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedRecords.map((record) => (
                                        <TableRow key={record.id}>
                                            <TableCell>
                                                <TypeBadge>{TYPE_LABELS[record.type] || record.type}</TypeBadge>
                                            </TableCell>
                                            <TableCell>
                                                <strong>{record.uniqueName || "-"}</strong>
                                            </TableCell>
                                            <TableCell>
                                                <ErrorText title={record.errorMessage}>
                                                    {record.errorMessage || "-"}
                                                </ErrorText>
                                            </TableCell>
                                            <TableCell>
                                                <GrupoText>{record.grupoId || "-"}</GrupoText>
                                            </TableCell>
                                            <TableCell>{formatDate(record.createdAt)}</TableCell>
                                            <TableActions>
                                                <ActionStack>
                                                    <IconActionButton
                                                        gradient={theme.gradient}
                                                        title="Visualizar detalhes"
                                                        onClick={() => handleViewDetail(record)}
                                                        disabled={actionLoading === record.id}
                                                    >
                                                        <Visibility style={{ fontSize: "18px" }} />
                                                    </IconActionButton>
                                                    <IconActionButton
                                                        gradient="linear-gradient(135deg, #27ae60 0%, #229954 100%)"
                                                        title="Reenviar XML"
                                                        onClick={() => handleResend(record)}
                                                        disabled={actionLoading === record.id}
                                                    >
                                                        <Refresh style={{ fontSize: "18px" }} />
                                                    </IconActionButton>
                                                    <DangerButton
                                                        title="Excluir registro"
                                                        onClick={() => setDeleteConfirm(record)}
                                                        disabled={actionLoading === record.id}
                                                    >
                                                        <Delete style={{ fontSize: "18px" }} />
                                                    </DangerButton>
                                                </ActionStack>
                                            </TableActions>
                                        </TableRow>
                                    ))
                                )}
                            </tbody>
                        </StreamsTable>
                    </TableWrapper>

                    <PaginationWrapper>
                        <RecordsPerPageContainer>
                            <RecordsPerPageLabel>Registros por página:</RecordsPerPageLabel>
                            <RecordsPerPageSelect
                                value={recordsPerPage}
                                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </RecordsPerPageSelect>
                        </RecordsPerPageContainer>

                        <PaginationContainer>
                            <PaginationButton
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            >
                                <NavigateBefore />
                            </PaginationButton>
                            <PageIndicator>
                                Página {currentPage} de {totalPages || 1}
                            </PageIndicator>
                            <PaginationButton
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            >
                                <NavigateNext />
                            </PaginationButton>
                        </PaginationContainer>
                    </PaginationWrapper>
                </ResultsCard>
            )}

            {/* Detail Modal */}
            {detailRecord && (
                <ModalOverlay onClick={() => setDetailRecord(null)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Detalhes do Erro</ModalTitle>
                            <CloseButton onClick={() => setDetailRecord(null)}>
                                <Close />
                            </CloseButton>
                        </ModalHeader>
                        <ModalBody>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailLabel>Tipo</DetailLabel>
                                    <DetailValue>
                                        <TypeBadge>{TYPE_LABELS[detailRecord.type] || detailRecord.type}</TypeBadge>
                                    </DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>UniqueName</DetailLabel>
                                    <DetailValue>{detailRecord.uniqueName || "-"}</DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Grupo ID</DetailLabel>
                                    <DetailValue>{detailRecord.grupoId || "-"}</DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>Data</DetailLabel>
                                    <DetailValue>{formatDate(detailRecord.createdAt)}</DetailValue>
                                </DetailItem>
                                <DetailItemFull>
                                    <DetailLabel>URL</DetailLabel>
                                    <DetailValue>
                                        {detailRecord.url ? (
                                            <DetailLink href={detailRecord.url} target="_blank" rel="noopener noreferrer">
                                                {detailRecord.url}
                                            </DetailLink>
                                        ) : (
                                            "-"
                                        )}
                                    </DetailValue>
                                </DetailItemFull>
                                <DetailItemFull>
                                    <DetailLabel>Mensagem de Erro</DetailLabel>
                                    <ErrorBlock>{detailRecord.errorMessage || "-"}</ErrorBlock>
                                </DetailItemFull>
                                <DetailItemFull>
                                    <DetailLabel>XML de Retorno</DetailLabel>
                                    {detailLoading ? (
                                        <XmlLoadingText>Carregando XML...</XmlLoadingText>
                                    ) : detailRecord.returnXml ? (
                                        <XmlBlock>{detailRecord.returnXml}</XmlBlock>
                                    ) : (
                                        <DetailValue>-</DetailValue>
                                    )}
                                </DetailItemFull>
                            </DetailGrid>
                        </ModalBody>
                        <ModalFooter>
                            <ModalActionButton
                                variant="success"
                                disabled={actionLoading === detailRecord.id}
                                onClick={() => handleResend(detailRecord)}
                            >
                                <Refresh style={{ fontSize: "18px", marginRight: "8px" }} />
                                {actionLoading === detailRecord.id ? "Reenviando..." : "Reenviar"}
                            </ModalActionButton>
                            <ModalActionButton
                                variant="danger"
                                disabled={actionLoading === detailRecord.id}
                                onClick={() => {
                                    setDetailRecord(null);
                                    setDeleteConfirm(detailRecord);
                                }}
                            >
                                <Delete style={{ fontSize: "18px", marginRight: "8px" }} />
                                Excluir
                            </ModalActionButton>
                            <ModalActionButton variant="secondary" onClick={() => setDetailRecord(null)}>
                                Fechar
                            </ModalActionButton>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <ModalOverlay onClick={() => setDeleteConfirm(null)}>
                    <ConfirmModal onClick={(e) => e.stopPropagation()}>
                        <ConfirmIcon>
                            <Delete style={{ fontSize: "48px", color: "#e74c3c" }} />
                        </ConfirmIcon>
                        <ConfirmTitle>Confirmar Exclusão</ConfirmTitle>
                        <ConfirmText>
                            Tem certeza que deseja remover este registro?
                            <br />
                            <strong>{deleteConfirm.uniqueName}</strong> ({TYPE_LABELS[deleteConfirm.type] || deleteConfirm.type})
                        </ConfirmText>
                        <ConfirmActions>
                            <ModalActionButton variant="secondary" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                            </ModalActionButton>
                            <ModalActionButton
                                variant="danger"
                                disabled={actionLoading === deleteConfirm.id}
                                onClick={() => handleDelete(deleteConfirm)}
                            >
                                {actionLoading === deleteConfirm.id ? "Removendo..." : "Sim, Excluir"}
                            </ModalActionButton>
                        </ConfirmActions>
                    </ConfirmModal>
                </ModalOverlay>
            )}
        </Container>
    );
}

export default ResponseErrors;

// ===== STYLED COMPONENTS =====

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const BackButton = styled.button`
    background: ${(props) => (props.color ? `${props.color}1A` : "rgba(230, 126, 34, 0.1)")};
    border: none;
    border-radius: 12px;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${(props) => props.color || "#e67e22"};
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => (props.color ? `${props.color}33` : "rgba(230, 126, 34, 0.2)")};
        transform: translateX(-4px);
    }

    &:active {
        transform: translateX(0);
    }
`;

// Filters
const FiltersCard = styled.div`
    max-width: 1200px;
    margin: 0 auto 24px;
    padding: 24px;
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    box-sizing: border-box;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-color: #d0d0d0;
    }

    @media (max-width: 768px) {
        margin: 0 16px 24px;
        padding: 16px;
    }
`;

const FiltersHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
`;

const FiltersTitle = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: #2c3e50;
    font-family: "Mulish", sans-serif;
`;

const FilterRow = styled.div`
    display: grid;
    grid-template-columns: 220px 220px 1fr;
    gap: 20px;
    align-items: end;

    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FilterLabel = styled.label`
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    font-family: "Mulish", sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const FilterSelectWrapper = styled.div`
    position: relative;

    &::after {
        content: '';
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #9ca3af;
        pointer-events: none;
    }
`;

const FilterSelect = styled.select`
    width: 100%;
    padding: 11px 36px 11px 14px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: "Mulish", sans-serif;
    background: #f9fafb;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #2c3e50;
    appearance: none;
    -webkit-appearance: none;

    &:focus {
        outline: none;
        border-color: #e67e22;
        box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.1);
        background: white;
    }

    &:hover {
        border-color: #d0d0d0;
        background: white;
    }
`;

const SectionInfoWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding: 24px 16px 0 16px;
    gap: 16px;
`;

const SectionInfoLeft = styled.div`
    display: flex;
    flex-direction: column;
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

const SearchIconWrapper = styled.div`
    position: absolute;
    left: 14px;
    color: #e67e22;
    display: flex;
    align-items: center;
    pointer-events: none;
    opacity: 0.7;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 11px 40px 11px 46px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: "Mulish", sans-serif;
    background: #f9fafb;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #e67e22;
        box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.1);
        background: white;
    }

    &:hover {
        border-color: #d0d0d0;
        background: white;
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 10px;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 50%;
    background: rgba(230, 126, 34, 0.1);
    color: #e67e22;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(230, 126, 34, 0.2);
        color: #d35400;
    }
`;

// Table extras
const TypeBadge = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: "Mulish", sans-serif;
    background: rgba(230, 126, 34, 0.1);
    color: #d35400;
    white-space: nowrap;
`;

const ErrorText = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;
    font-size: 13px;
    color: #e74c3c;
`;

const GrupoText = styled.span`
    font-size: 12px;
    color: #64748b;
    font-family: monospace;
    word-break: break-all;
`;

// Pagination
const PaginationWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
`;

const RecordsPerPageContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RecordsPerPageLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-family: "Mulish", sans-serif;
`;

const RecordsPerPageSelect = styled.select`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: "Mulish", sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #e67e22;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
`;

const PaginationButton = styled.button`
    width: 40px;
    height: 40px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(230, 126, 34, 0.3);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(230, 126, 34, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        background: #bdbdbd;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const PageIndicator = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    font-family: "Mulish", sans-serif;
    padding: 0 16px;
`;

// Modal
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 0.3s ease;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e8e8e8;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    font-family: "Mulish", sans-serif;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #6b7280;
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        background: #f3f4f6;
        color: #374151;
    }
`;

const ModalBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex: 1;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #e8e8e8;
`;

const getModalButtonStyles = (variant) => {
    const variants = {
        success: `
            background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
            color: white;
            &:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4); }
        `,
        danger: `
            background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
            color: white;
            &:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4); }
        `,
        secondary: `
            background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
            color: white;
            &:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(149, 165, 166, 0.4); }
        `,
    };
    return variants[variant] || variants.secondary;
};

const ModalActionButton = styled.button`
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: "Mulish", sans-serif;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: all 0.3s ease;
    ${(props) => getModalButtonStyles(props.variant)}

    &:hover:not(:disabled) {
        transform: translateY(-2px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

// Detail grid
const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DetailItemFull = styled(DetailItem)`
    grid-column: 1 / -1;
`;

const DetailLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-family: "Mulish", sans-serif;
`;

const DetailValue = styled.span`
    font-size: 14px;
    color: #2c3e50;
    font-family: "Mulish", sans-serif;
    word-break: break-word;
`;

const DetailLink = styled.a`
    color: #e67e22;
    text-decoration: none;
    word-break: break-all;

    &:hover {
        text-decoration: underline;
    }
`;

const ErrorBlock = styled.div`
    padding: 12px 16px;
    background: rgba(231, 76, 60, 0.06);
    border: 1px solid rgba(231, 76, 60, 0.15);
    border-radius: 8px;
    color: #c0392b;
    font-size: 13px;
    font-family: "Mulish", sans-serif;
    line-height: 1.5;
    word-break: break-word;
`;

const XmlBlock = styled.pre`
    padding: 16px;
    background: #1e293b;
    border-radius: 10px;
    color: #e2e8f0;
    font-size: 12px;
    font-family: "Consolas", "Monaco", "Courier New", monospace;
    line-height: 1.6;
    overflow-x: auto;
    max-height: 400px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #334155;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #64748b;
        border-radius: 4px;
    }
`;

const XmlLoadingText = styled.span`
    font-size: 14px;
    color: #9ca3af;
    font-style: italic;
`;

// Confirm Modal
const ConfirmModal = styled.div`
    background: white;
    border-radius: 16px;
    padding: 32px;
    width: 90%;
    max-width: 420px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 0.3s ease;
`;

const ConfirmIcon = styled.div`
    margin-bottom: 16px;
`;

const ConfirmTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    font-family: "Mulish", sans-serif;
`;

const ConfirmText = styled.p`
    margin: 0 0 24px 0;
    font-size: 14px;
    color: #6b7280;
    font-family: "Mulish", sans-serif;
    line-height: 1.6;
`;

const ConfirmActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
`;
